/**
 * Centralized configuration module
 * All environment-specific values should come through here
 */

interface Config {
  api: {
    baseUrl: string;
    socketUrl: string;
  };
  services: {
    openWeatherApiKey: string;
    openWeatherApiUrl: string;
    w3wApiKey: string;
    w3wApiUrl: string;
    avwxApiKey: string;
    avwxApiUrl: string;
    mapboxApiKey: string;
    nominatimApiUrl: string;
    noaaWeatherApiUrl: string;
    canadaWeatherApiUrl: string;
    tileServerUrl: string;
  };
  features: {
    enableDebug: boolean;
    enableAnalytics: boolean;
    enableErrorTracking: boolean;
  };
  app: {
    name: string;
    version: string;
    environment: string;
    userAgent: string;
  };
  timeouts: {
    default: number;
    weather: number;
    geocoding: number;
  };
  map: {
    defaultCenter: {
      lat: number;
      lng: number;
    };
    defaultZoom: number;
  };
}

// Get config value with fallback
const getEnvVar = (key: string, defaultValue = ''): string => {
  return import.meta.env[key] || defaultValue;
};

const getBooleanEnvVar = (key: string, defaultValue = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === true;
};

const getNumberEnvVar = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

const getFloatEnvVar = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const config: Config = {
  api: {
    baseUrl: getEnvVar('VITE_API_URL', '/api'),
    socketUrl: getEnvVar('VITE_SOCKET_URL', ''),
  },
  services: {
    openWeatherApiKey: getEnvVar('VITE_OPENWEATHER_API_KEY'),
    openWeatherApiUrl: getEnvVar('VITE_OPENWEATHER_API_URL', 'https://api.openweathermap.org'),
    w3wApiKey: getEnvVar('VITE_W3W_API_KEY'),
    w3wApiUrl: getEnvVar('VITE_W3W_API_URL', 'https://api.what3words.com/v3'),
    avwxApiKey: getEnvVar('VITE_AVWX_API_KEY'),
    avwxApiUrl: getEnvVar('VITE_AVWX_API_URL', 'https://avwx.rest/api'),
    mapboxApiKey: getEnvVar('VITE_MAPBOX_API_KEY'),
    nominatimApiUrl: getEnvVar('VITE_NOMINATIM_API_URL', 'https://nominatim.openstreetmap.org'),
    noaaWeatherApiUrl: getEnvVar('VITE_NOAA_WEATHER_API_URL', 'https://aviationweather.gov/cgi-bin/data/dataserver.php'),
    canadaWeatherApiUrl: getEnvVar('VITE_CANADA_WEATHER_API_URL', 'https://dd.weather.gc.ca/observations/xml/BC/hourly'),
    tileServerUrl: getEnvVar('VITE_TILE_SERVER_URL', 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
  },
  features: {
    enableDebug: getBooleanEnvVar('VITE_ENABLE_DEBUG'),
    enableAnalytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS'),
    enableErrorTracking: getBooleanEnvVar('VITE_ENABLE_ERROR_TRACKING'),
  },
  app: {
    name: getEnvVar('VITE_APP_NAME', 'TEAAM EMS'),
    version: getEnvVar('VITE_APP_VERSION', '1.0.0'),
    environment: getEnvVar('VITE_APP_ENV', 'development'),
    userAgent: getEnvVar('VITE_USER_AGENT', 'TEAAM-EMS-System/1.0'),
  },
  timeouts: {
    default: getNumberEnvVar('VITE_DEFAULT_REQUEST_TIMEOUT', 5000),
    weather: getNumberEnvVar('VITE_WEATHER_REQUEST_TIMEOUT', 10000),
    geocoding: getNumberEnvVar('VITE_GEOCODING_REQUEST_TIMEOUT', 5000),
  },
  map: {
    defaultCenter: {
      lat: getFloatEnvVar('VITE_MAP_DEFAULT_CENTER_LAT', 49.7016),
      lng: getFloatEnvVar('VITE_MAP_DEFAULT_CENTER_LNG', -123.1558),
    },
    defaultZoom: getNumberEnvVar('VITE_MAP_DEFAULT_ZOOM', 8),
  },
};

// Debug logging in development
if (config.features.enableDebug) {
  console.log('App Configuration:', config);
}

export default config;